// The sitar style's band (docs/AUDIO_DESIGN.md §12): a harmonium (the chord instrument, with the tanpura drone inside it,
// so "Chords only" keeps the drone), a tabla kit (dayan, bayan, a soft lofi kick and snare under them, ghungroo bells) and
// a soft, sparse sub bass. Drums and bass are persistent voices gated with setTargetAtTime, like instruments.js.
import { hash } from '../../engine/core.js';
import { mtof, hold, noise } from '../dsp.js';
import { LEVELS, gain, filter, osc, panner, persistent } from '../instruments.js';
import { tanpuraLoop } from './sitar-voice.js';

const mod12 = n => ((n % 12) + 12) % 12;
const stopAt = (s, t) => { try { s.stop(t); } catch { /* not started or already stopped */ } };

/* ---------------- harmonium: two free reeds a note, bellows, and the tanpura ---------------- */
// Levels (tuning knobs): a reed pair's peak, the bus, its reverb send, the tanpura and its send.
export const HARM = { amp: 0.2, level: 1, send: 0.26, tanpura: 0.3, tSend: 0.35 };
const REEDS = new WeakMap();
/** A free reed swings through its slot and chops the air into narrow pulses: harmonics n^−1 · (.4 + .6·|sin .3πn|)
 *  (no zero, an uneven comb: the reedy, nasal colour) up to 48, band-limited by the browser. One wave per context. */
function reedWave(ac) {
  let w = REEDS.get(ac);
  if (!w) {
    const n = 48, re = new Float32Array(n + 1), im = new Float32Array(n + 1);
    for (let k = 1; k <= n; k++) im[k] = (0.4 + 0.6 * Math.abs(Math.sin(0.3 * Math.PI * k))) / k;
    w = ac.createPeriodicWave(re, im);
    REEDS.set(ac, w);
  }
  return w;
}

/**
 * createHarmonium(ac, out, send, plan, opts) → { chord, pcsAt, stats, dispose }   (the style's `ep`)
 * plan.ep.lp: the bus LP; plan.harm: { det (¢ between the reeds), bellows (Hz), depth, nasal (dB), atk (τ, s) };
 * plan.tanpura: { level, offset }. opts: { registry, tag, cap, r, t0, log, stepDur, onChord }.
 * Per note two oscillators on the reed wave, ∓det/2 ¢ (5–10 ¢ apart, ±1 ¢ per pitch: the beating of a double-reed
 * harmonium) → a gain: in with τ atk (18–34 ms) to .85 of 0.2·v^1.2, the bellows push to the full level (τ .3 s), out with
 * the event's release (τ ≥ 60 ms; the final chord's 0.9 s). One voice per pitch: a key pressed again while it sounds dips
 * to .4 for ~20 ms and speaks again (a common tone does the same); one in its release swells back. Over the cap the voice
 * released first goes (30 ms). Bus: 3 panners (−.14 · 0 · +.14; under MIDI 55 in the centre) → nasal peak 1.15 kHz
 * (+1.5–3.5 dB, Q 1) → LP plan.ep.lp (Q .6) → bellows (an LFO at .15–.27 Hz, ±depth) → out, reverb .26.
 * Tanpura: the looped stereo buffer (tanpuraLoop, from a seeded point) → LP 3.2 kHz → out ×0.3·level, reverb .35; it comes
 * in with the track's first chord (τ 1.6 s) and goes out over the final chord (from half-way, τ a quarter of it).
 */
export function createHarmonium(ac, out, send, plan, { registry, tag, cap = 16, r = Math.random, t0 = ac.currentTime, log = null, onChord = null }) {
  const H = plan.harm, T = plan.tanpura, nodes = [], src = [], N = x => (nodes.push(x), x), wave = reedWave(ac);
  const pans = [-0.14, 0, 0.14].map(p => N(panner(ac, p))), sum = N(gain(ac, 1));
  const nasal = N(filter(ac, 'peaking', 1150, 1, H.nasal)), lp = N(filter(ac, 'lowpass', plan.ep.lp, 0.6));
  const bellows = N(gain(ac, 1)), outG = N(gain(ac, HARM.level)), sendG = N(gain(ac, HARM.send));
  for (const p of pans) p.connect(sum);
  sum.connect(nasal); nasal.connect(lp); lp.connect(bellows); bellows.connect(outG); outG.connect(out); outG.connect(sendG); sendG.connect(send);
  const lfo = N(osc(ac, 'sine', H.bellows)), lfoG = N(gain(ac, H.depth));
  lfo.connect(lfoG); lfoG.connect(bellows.gain); src.push(lfo);
  const loop = tanpuraLoop(ac, plan.key.tonic), tS = N(ac.createBufferSource());
  tS.buffer = loop; tS.loop = true;
  const tAmp = N(gain(ac, 0)), tLP = N(filter(ac, 'lowpass', 3200, 0.6)), tSend = N(gain(ac, HARM.tSend)), tLevel = HARM.tanpura * T.level;
  tS.connect(tLP); tLP.connect(tAmp); tAmp.connect(out); tAmp.connect(tSend); tSend.connect(send);
  src.push(tS);
  const bus = persistent(registry, tag, src, nodes, t0, (T.offset || 0) * loop.duration);
  let voices = [], recs = [], maxActive = 0, steals = 0, notes = 0, started = false;

  const steal = t => {
    let k = 0;
    for (let i = 1; i < voices.length; i++) if (voices[i].off < voices[k].off) k = i;
    const old = voices.splice(k, 1)[0];
    registry.stop(old.v, t, 0.01);
    old.end = t; if (old.rec) old.rec[1] = Math.max(old.rec[0], t);
    steals++;
  };
  function note(t, midi, vel, dur, relTau) {
    const A = HARM.amp * Math.pow(vel, 1.2), off = t + Math.max(0.06, dur), end = off + 6 * relTau;
    voices = voices.filter(x => x.end > t);
    const y = voices.find(x => x.midi === midi);
    if (y) {
      const g = y.amp.gain;
      hold(g, t);
      if (t < y.off - 0.01) g.setTargetAtTime(0.4 * y.A, t, 0.006);                   // the key again: the reed stops a moment
      g.setTargetAtTime(0.85 * A, t + 0.02, H.atk); g.setTargetAtTime(A, t + 0.06, 0.3);
      g.setTargetAtTime(0, off, relTau);
      for (const o of y.v.src) stopAt(o, end);
      y.v.end = end; y.A = A; y.off = off; y.end = off + 4 * relTau;
      if (y.rec) y.rec[1] = Math.max(y.rec[0], Math.min(y.rec[1], t));
      y.rec = log ? [t, y.end] : null;
      if (y.rec) log.push(y.rec);
      notes++;
      return;
    }
    if (voices.length >= cap) steal(t);
    const c = H.det / 2, j = 2 * hash(midi, 71) - 1, o1 = osc(ac, 'sine', mtof(midi)), o2 = osc(ac, 'sine', mtof(midi)), amp = gain(ac, 0);
    o1.setPeriodicWave(wave); o2.setPeriodicWave(wave);
    o1.detune.value = -c + j; o2.detune.value = c + j * 0.5;
    o1.connect(amp); o2.connect(amp); amp.connect(pans[midi < 55 ? 1 : midi % 2 ? 0 : 2]);
    amp.gain.setValueAtTime(0, t);
    amp.gain.setTargetAtTime(0.85 * A, t, H.atk); amp.gain.setTargetAtTime(A, t + 0.06, 0.3);
    amp.gain.setTargetAtTime(0, off, relTau);
    o1.start(t); o2.start(t); o1.stop(end); o2.stop(end);
    const v = registry.add({ src: [o1, o2], all: [o1, o2, amp], start: t, end, tag, out: amp });
    const x = { v, midi, amp, A, off, end: off + 4 * relTau, rec: log ? [t, off + 4 * relTau] : null };
    if (x.rec) log.push(x.rec);
    voices.push(x);
    notes++;
    maxActive = Math.max(maxActive, voices.length);
  }
  return {
    /** pressed bottom to top over `strum` (one hand: 8–24 ms), the top +6 %; rel ≥ 0.5 (the final chord) sets the release */
    chord(t, midis, vel, dur, strum = 0.015, rel = 0.08) {
      const n = midis.length, relTau = Math.max(0.06, rel);
      midis.forEach((m, k) => {
        const dt = n > 1 ? (k * strum) / (n - 1) : 0;
        note(t + dt, m, Math.min(1, vel * (k === n - 1 && n > 1 ? 1.06 : 1)), dur - dt, relTau);
      });
      recs = recs.filter(c => c.end > t - 1);
      recs.push({ t, end: t + dur, pcs: midis.map(mod12) });
      if (!started) { started = true; tAmp.gain.setTargetAtTime(tLevel, t, 1.6); }
      if (rel >= 0.5) tAmp.gain.setTargetAtTime(0, t + 0.5 * dur, 0.25 * dur);
      if (onChord) onChord();
    },
    /** the pitch classes the harmonium holds at t (every chord event sounding then), or null */
    pcsAt(t) {
      const s = new Set();
      for (const c of recs) if (t >= c.t - 0.05 && t < c.end) for (const p of c.pcs) s.add(p);
      return s.size ? s : null;
    },
    stats: () => ({ maxActive, steals, notes, active: voices.length }),
    dispose(t) {
      const a = Math.max(ac.currentTime, t - 4);                 // the drone is out before the bus is freed
      hold(tAmp.gain, a); tAmp.gain.setTargetAtTime(0, a, 0.5);
      bus.dispose(t);
    },
  };
}

/* ---------------- tabla: dayan (tuned to Sa), bayan (with its swoop), a lofi kick and snare, ghungroo ---------------- */
// Levels (tuning knobs) against the jazzhop kit: the whole kit, then each drum.
export const TABLA = { level: 0.9, dayan: 1, bayan: 0.8, kick: 0.7, snare: 0.6, bells: 1 };
const MODES = [1, 2, 3.01];                           // the dayan's syahi makes its first modes harmonic (Raman)
//                 mode amplitudes         decays (s; na/tin: × the plan's)   pitch bend · finger click
const NA = { a: [0.08, 0.3, 0.2], d: [0.1, 1, 0.6], bend: 0.012, click: 0.12 };
const TIN = { a: [0.2, 0.26, 0.1], d: [1, 0.8, 0.45], bend: 0.008, click: 0.05 };
const TI = { a: [0.12, 0.08, 0.03], d: [0.035, 0.028, 0.02], bend: 0, click: 0 };
/**
 * createTabla(ac, out, send, kit, opts) → the kit. kit: { dayan (MIDI: Sa at 59–70), bayanF, swoop (odds), rise
 * (semitones), naDecay, tinDecay, geDecay, kickF0, kickF1, kickDecay, snareF, snareDecay, lp, sd (s a step), seed }.
 * Lanes: H = the dayan's na (x) and tin (o) · R = ti (x) and te (g) · S = a soft lofi snare (x), te (g) · T = the bayan's
 * ge (x; g soft, no swoop) · W = ka (the bayan closed with a flat hand, which also stops a ringing ge) · K = a soft
 * lofi kick · X = ghungroo bells. A dha (dhin) is a na (tin) and a ge on the same step.
 *  dayan: 3 sines at Sa × 1 · 2 · 3.01, bent down from +1.2 % (τ 30 ms). na: .08 .3 .2 × v, decays .1 s, naDecay
 *         (.28–.4 s) and .6 of it, a finger click (noise BP 3.2 kHz, .12v, τ 5 ms); tin: .2 .26 .1, tinDecay (.45–.65 s),
 *         .8 of it and .45 s; ti: .12 .08 .03, τ 20–35 ms + a slap (the same noise band at 1.5 kHz, .3v, τ 18 ms); te
 *         is ti ×.6 with the slap at 2.3 kHz. Pan +.12.
 *  bayan: sine bayanF (84–104 Hz) + its 2nd mode 1.52 f (.12v, τ 80 ms), .55v, decay geDecay (.3–.45 s), a palm thump
 *         (noise BP 260 Hz, .25v, τ 14 ms). The swoop (on `swoop` of the ge strokes, 25–55 %, the same bar and step every
 *         time): the wrist presses the skin and the pitch rises `rise` semitones (3–6) from 30 ms (τ 90 ms); else it lifts
 *         3 % (τ 120 ms). ka: the noise band at 650 Hz (.4v, τ 20 ms) + a dead thud (.15v, τ 25 ms). Pan −.12.
 *  kick: sine kickF0 (100–118) → kickF1 (45–51) Hz, τ 35 ms, .5v ×.7, decay τ kickDecay (.14–.19 s), no click.
 *  snare: noise → BP snareF (1.4–1.9 kHz, Q .55) .3v ×.6, τ snareDecay (.08–.12): papery and far.
 *  ghungroo: noise → BP 8.6 kHz (Q 2), 3 bursts 0 / 14 / 30 ms, .1v, τ 45 ms, with the dayan.
 * Everything through the bus LP kit.lp; the drums (not the kick) send .15 to the reverb. One noise source, 25 nodes.
 */
export function createTabla(ac, out, send, kit, { registry, tag, t0 = ac.currentTime }) {
  const L = LEVELS.kit * TABLA.level, nodes = [], N = x => (nodes.push(x), x);
  const src = N(ac.createBufferSource());
  src.buffer = noise(ac, 'white'); src.loop = true;
  const bus = N(filter(ac, 'lowpass', kit.lp, 0.5)), sendG = N(gain(ac, 0.15));
  bus.connect(out); sendG.connect(send);
  const fd = mtof(kit.dayan), dPan = N(panner(ac, 0.12)), bPan = N(panner(ac, -0.12));
  for (const p of [dPan, bPan]) { p.connect(bus); p.connect(sendG); }
  const dO = MODES.map(k => N(osc(ac, 'sine', fd * k))), dG = MODES.map(() => N(gain(ac, 0)));
  dO.forEach((o, i) => { o.connect(dG[i]); dG[i].connect(dPan); });
  const dBP = N(filter(ac, 'bandpass', 3200, 1)), dN = N(gain(ac, 0)), jBP = N(filter(ac, 'bandpass', 8600, 2)), jAmp = N(gain(ac, 0));
  src.connect(dBP); dBP.connect(dN); dN.connect(dPan); src.connect(jBP); jBP.connect(jAmp); jAmp.connect(dPan);
  const bO = N(osc(ac, 'sine', kit.bayanF)), bG = N(gain(ac, 0)), bO2 = N(osc(ac, 'sine', 1.52 * kit.bayanF)), bG2 = N(gain(ac, 0));
  const bBP = N(filter(ac, 'bandpass', 260, 0.8)), bN = N(gain(ac, 0));
  bO.connect(bG); bG.connect(bPan); bO2.connect(bG2); bG2.connect(bPan); src.connect(bBP); bBP.connect(bN); bN.connect(bPan);
  const kO = N(osc(ac, 'sine', kit.kickF1)), kG = N(gain(ac, 0)), snBP = N(filter(ac, 'bandpass', kit.snareF, 0.55)), snN = N(gain(ac, 0));
  kO.connect(kG); kG.connect(bus); src.connect(snBP); snBP.connect(snN); snN.connect(bus); snN.connect(sendG);
  const p = persistent(registry, tag, [src, ...dO, bO, bO2, kO], nodes, t0, 1.9);
  const hit = (param, peak, t, atk, rel, tau) => { param.setTargetAtTime(peak, t, atk); param.setTargetAtTime(0, t + rel, tau); };
  const D = TABLA.dayan * L, B = TABLA.bayan * L;
  /** one stroke's noise on a shared band (the finger's click, a slap, the palm, a ka): its centre, Q and level */
  const band = (bp, g, t, f, q, peak, tau) => { bp.frequency.setValueAtTime(f, t); bp.Q.setValueAtTime(q, t); hit(g.gain, peak, t, 0.0006, 0.002, tau); };
  function dayan(t, v, S, decays) {
    dO.forEach((o, i) => {
      o.frequency.setValueAtTime(fd * MODES[i] * (1 + S.bend), t); o.frequency.setTargetAtTime(fd * MODES[i], t, 0.03);
      hit(dG[i].gain, S.a[i] * v * D, t, 0.0008, 0.003, decays[i]);
    });
    if (S.click) band(dBP, dN, t, 3200, 1, S.click * v * D, 0.005);
  }
  const ti = (t, v, soft) => {
    dayan(t, v * (soft ? 0.6 : 1), TI, TI.d);
    band(dBP, dN, t, soft ? 2300 : 1500, 0.9, 0.3 * v * (soft ? 0.6 : 1) * D, 0.018);
  };
  const stepOf = e => Math.round(e.t / kit.sd - e.bar * 16);
  return {
    kick(t, v) {
      kO.frequency.setValueAtTime(kit.kickF0, t); kO.frequency.setTargetAtTime(kit.kickF1, t, 0.035);
      hit(kG.gain, 0.5 * v * L * TABLA.kick, t, 0.002, 0.008, kit.kickDecay);
    },
    snare(t, v, ghost = false) {
      if (ghost) ti(t, v, true);
      else hit(snN.gain, 0.3 * v * L * TABLA.snare, t, 0.0015, 0.005, kit.snareDecay);
    },
    hat(t, v, open = false) {
      if (open) dayan(t, v, TIN, [kit.tinDecay, 0.8 * kit.tinDecay, TIN.d[2]]);
      else dayan(t, v, NA, [NA.d[0], kit.naDecay, NA.d[2] * kit.naDecay]);
    },
    rim(t, v, e) { ti(t, v, !!(e && e.ghost)); },
    tom(t, v, e) {
      const soft = !!(e && e.ghost), swoop = !soft && !!e && hash(kit.seed, e.bar, stepOf(e)) < kit.swoop, f0 = kit.bayanF;
      for (const [o, k] of [[bO, 1], [bO2, 1.52]]) {
        o.frequency.setValueAtTime(f0 * k, t);
        o.frequency.setTargetAtTime(f0 * k * (swoop ? Math.pow(2, kit.rise / 12) : 1.03), t + (swoop ? 0.03 : 0.01), swoop ? 0.09 : 0.12);
      }
      const a = soft ? 0.55 : 1;
      hit(bG.gain, 0.55 * v * a * B, t, 0.002, 0.006, kit.geDecay * (soft ? 0.6 : swoop ? 1.25 : 1));
      hit(bG2.gain, 0.12 * v * a * B, t, 0.002, 0.004, 0.08);
      band(bBP, bN, t, 260, 0.8, 0.25 * v * a * B, 0.014);
    },
    block(t, v) {
      bO.frequency.setValueAtTime(kit.bayanF * 1.1, t);
      hit(bG.gain, 0.15 * v * B, t, 0.002, 0.004, 0.025);
      hit(bG2.gain, 0, t, 0.004, 0.004, 0.01);
      band(bBP, bN, t, 650, 1.2, 0.4 * v * B, 0.02);
    },
    shaker(t, v) { [[0, 1], [0.014, 0.6], [0.03, 0.4]].forEach(([d, w], k) => hit(jAmp.gain, 0.1 * v * w * L * TABLA.bells, t + d, 0.0006, 0.002, k === 2 ? 0.045 : 0.01)); },
    dispose(t) { p.dispose(t); },
  };
}

/* ---------------- soft bass: a round sub, sparse ---------------- */
export const SUB = { level: 0.65 };
/** sine + triangle (.18) → LP p.lp (300–400 Hz, Q .6) → amp: in τ 15 ms to 0.42v (no thump), to .7 over τ .35 s, a long
 *  ring (τ 1.6 s from 0.5 s), out τ 90 ms; a slide comes up a whole tone (τ 40 ms). The tanpura and the bayan carry the
 *  rest of the low end. p: { lp } */
export function createSoftBass(ac, out, p, { registry, tag, t0 = ac.currentTime }) {
  const sine = osc(ac, 'sine', 55), tri = osc(ac, 'triangle', 55), triG = gain(ac, 0.18), lp = filter(ac, 'lowpass', p.lp || 350, 0.6), amp = gain(ac, 0);
  sine.connect(lp); tri.connect(triG); triG.connect(lp); lp.connect(amp); amp.connect(out);
  const v = persistent(registry, tag, [sine, tri], [sine, tri, triG, lp, amp], t0);
  return {
    note(t, midi, vel, dur, slide = false) {
      const f = mtof(midi), A = 0.42 * vel * SUB.level, off = t + Math.max(0.1, dur);
      for (const prm of [sine.frequency, tri.frequency, amp.gain]) hold(prm, t);
      for (const o of [sine, tri]) {
        if (slide) { o.frequency.setValueAtTime(f * Math.pow(2, -2 / 12), t); o.frequency.setTargetAtTime(f, t, 0.04); }
        else o.frequency.setValueAtTime(f, t);
      }
      amp.gain.setTargetAtTime(A, t, 0.015);
      amp.gain.setTargetAtTime(0.7 * A, t + 0.05, 0.35);
      if (off > t + 0.5) amp.gain.setTargetAtTime(0, t + 0.5, 1.6);
      amp.gain.setTargetAtTime(0, off, 0.09);
    },
    dispose(t) { v.dispose(t); },
  };
}
